import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';

const HistorialDashboard = () => {
  const { isDarkMode } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [mcpConnections, setMcpConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [userList, setUserList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('loginTime-desc');
  const [activeSubTab, setActiveSubTab] = useState('logins'); // logins, mcp-connections
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch sequentially to avoid rate limiting
      await fetchUsers();
      await fetchSessions();
      await fetchMCPConnections();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch history data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async () => {
    try {
      setDeleting(true);
      const response = await apiClient.delete('/login-history/admin/all');
      
      if (response.data.success) {
        // Clear local state
        setSessions([]);
        setMcpConnections([]);
        setShowDeleteConfirm(false);
        setSuccessMessage(`Successfully deleted ${response.data.deletedCount} history records`);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Error deleting history:', err);
      setError('Failed to delete history. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveHistory = () => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      let savedCount = 0;
      
      // Always save both logins and MCP connections
      // Save logins data as CSV
      const loginsColumns = [
        { field: 'user.username', label: 'Username' },
        { field: 'user.email', label: 'Email' },
        { field: 'timestamp', label: 'Login Time' },
        { field: 'logoutEvent.timestamp', label: 'Logout Time' },
        { field: 'connectionDurationMs', label: 'Duration' }
      ];
      if (processedSessions.length > 0) {
        const loginsCSV = convertToCSV(processedSessions, loginsColumns);
        downloadCSV(loginsCSV, `logins_${timestamp}.csv`);
        savedCount++;
      }
      
      // Save MCP connections data as CSV with a slight delay to avoid download prompt
      const connectionsColumns = [
        { field: 'user.username', label: 'Username' },
        { field: 'user.email', label: 'Email' },
        { field: 'timestamp', label: 'Connect Time' },
        { field: 'disconnectEvent.timestamp', label: 'Disconnect Time' },
        { field: 'connectionDurationMs', label: 'Duration' }
      ];
      if (processedMCPConnections.length > 0) {
        setTimeout(() => {
          const connectionsCSV = convertToCSV(processedMCPConnections, connectionsColumns);
          downloadCSV(connectionsCSV, `mcp_connections_${timestamp}.csv`);
        }, 200); // 200ms delay between downloads
        savedCount++;
      }
      
      if (savedCount === 0) {
        setError('No history data to export.');
      } else if (savedCount === 2) {
        setSuccessMessage('History exported successfully (2 files: logins + connections)');
      } else if (savedCount === 1) {
        setSuccessMessage('History exported successfully (1 file)');
      }
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving history:', err);
      setError('Failed to save history. Please try again.');
    }
  };

  const convertToCSV = (data, columns) => {
    if (data.length === 0) return '';

    // Get nested property value
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((current, prop) => current?.[prop], obj);
    };

    // Headers - use label if provided, otherwise format the field name
    const headers = columns.map(col => {
      if (typeof col === 'object') {
        return col.label;
      }
      const parts = col.split('.');
      return parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1);
    });

    // Rows
    const rows = data.map(row =>
      columns.map(col => {
        const fieldPath = typeof col === 'object' ? col.field : col;
        let value = getNestedValue(row, fieldPath);
        
        if (fieldPath === 'connectionDurationMs' && value) {
          return formatDuration(value);
        } else if ((fieldPath === 'timestamp' || fieldPath === 'logoutEvent.timestamp' || fieldPath === 'disconnectEvent.timestamp') && value) {
          return formatDate(value);
        } else if ((fieldPath === 'logoutEvent.timestamp' || fieldPath === 'disconnectEvent.timestamp') && !value) {
          // Check if this is an active session (no logout/disconnect)
          if (row.isActive) {
            return 'Still Active';
          }
          return 'N/A';
        } else if (Array.isArray(value)) {
          return value.map(v => (typeof v === 'object' ? v.name || v.id : v)).join('; ');
        } else if (value === null || value === undefined) {
          return 'N/A';
        }
        return String(value);
      })
    );

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users');
      setUserList(response.data.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await apiClient.get('/login-history/admin/all', {
        params: { limit: 200 }
      });
      // Filter for login/logout events only
      const allHistory = response.data.data || [];
      let loginLogoutEvents = allHistory.filter(h => h.eventType === 'login' || h.eventType === 'logout');
      
      // Process sessions: pair logins with logouts and infer logouts for unpaired logins
      const processedSessions = [];
      const userLogins = {}; // Track unpaired logins per user
      
      // Sort events by timestamp
      loginLogoutEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      loginLogoutEvents.forEach(event => {
        if (event.eventType === 'login') {
          // Store unpaired login
          if (!userLogins[event.userId]) {
            userLogins[event.userId] = [];
          }
          userLogins[event.userId].push(event);
        } else if (event.eventType === 'logout') {
          // Pair logout with most recent unpaired login
          if (userLogins[event.userId] && userLogins[event.userId].length > 0) {
            const loginEvent = userLogins[event.userId].shift();
            // Create a paired session entry
            processedSessions.push({
              ...loginEvent,
              logoutEvent: event,
              connectionDurationMs: new Date(event.timestamp) - new Date(loginEvent.timestamp),
              isPaired: true
            });
          } else {
            // Logout without corresponding login
            processedSessions.push({
              ...event,
              isPaired: false
            });
          }
        }
      });
      
      // Add unpaired logins - only the most recent one per user is active
      Object.keys(userLogins).forEach(userId => {
        const unpairLogins = userLogins[userId];
        
        unpairLogins.forEach((loginEvent, index) => {
          const isLastLogin = index === unpairLogins.length - 1;
          const loginTime = new Date(loginEvent.timestamp);
          const now = new Date();
          const hoursAgo = (now - loginTime) / (1000 * 60 * 60);
          
          if (hoursAgo < 24) {
            // If this is not the last login, infer a logout at the time of the next login
            if (!isLastLogin) {
              const nextLoginTime = new Date(unpairLogins[index + 1].timestamp);
              processedSessions.push({
                ...loginEvent,
                logoutEvent: unpairLogins[index + 1],
                connectionDurationMs: nextLoginTime - loginTime,
                isPaired: false,
                isInferred: true // Inferred logout
              });
            } else {
              // Only the most recent login is active
              processedSessions.push({
                ...loginEvent,
                connectionDurationMs: now - loginTime,
                isPaired: false,
                isActive: true // Mark as active session
              });
            }
          }
        });
      });
      
      setSessions(processedSessions);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  };

  const fetchMCPConnections = async () => {
    try {
      const response = await apiClient.get('/login-history/admin/all', {
        params: { limit: 200 }
      });
      // Filter for MCP proxy connections
      const allHistory = response.data.data || [];
      let mcpEvents = allHistory.filter(h => 
        h.eventType === 'mcp_proxy_connect' || h.eventType === 'mcp_proxy_disconnect'
      );
      
      // Process connections: pair connects with disconnects
      const processedConnections = [];
      const userConnections = {}; // Track unpaired connects per user
      
      // Sort events by timestamp
      mcpEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      mcpEvents.forEach(event => {
        // Parse JSON fields if they exist
        let parsedEvent = { ...event };
        if (parsedEvent.accessedMcpServers && typeof parsedEvent.accessedMcpServers === 'string') {
          try {
            parsedEvent.accessedMcpServers = JSON.parse(parsedEvent.accessedMcpServers);
          } catch (e) {
            parsedEvent.accessedMcpServers = [];
          }
        }
        if (parsedEvent.accessedWorkflows && typeof parsedEvent.accessedWorkflows === 'string') {
          try {
            parsedEvent.accessedWorkflows = JSON.parse(parsedEvent.accessedWorkflows);
          } catch (e) {
            parsedEvent.accessedWorkflows = [];
          }
        }
        
        if (parsedEvent.eventType === 'mcp_proxy_connect') {
          // Store unpaired connect
          if (!userConnections[parsedEvent.userId]) {
            userConnections[parsedEvent.userId] = [];
          }
          userConnections[parsedEvent.userId].push(parsedEvent);
        } else if (parsedEvent.eventType === 'mcp_proxy_disconnect') {
          // Pair disconnect with most recent unpaired connect
          if (userConnections[parsedEvent.userId] && userConnections[parsedEvent.userId].length > 0) {
            const connectEvent = userConnections[parsedEvent.userId].shift();
            // Use disconnect event's accessedMcpServers/accessedWorkflows if available, otherwise use connect event's
            const accessedMcpServers = parsedEvent.accessedMcpServers && parsedEvent.accessedMcpServers.length > 0 
              ? parsedEvent.accessedMcpServers 
              : (connectEvent.accessedMcpServers || []);
            const accessedWorkflows = parsedEvent.accessedWorkflows && parsedEvent.accessedWorkflows.length > 0 
              ? parsedEvent.accessedWorkflows 
              : (connectEvent.accessedWorkflows || []);
            
            // Create a paired connection entry
            processedConnections.push({
              ...connectEvent,
              disconnectEvent: parsedEvent,
              accessedMcpServers,
              accessedWorkflows,
              connectionDurationMs: new Date(parsedEvent.timestamp) - new Date(connectEvent.timestamp),
              isPaired: true
            });
          } else {
            // Disconnect without corresponding connect
            processedConnections.push({
              ...parsedEvent,
              isPaired: false
            });
          }
        }
      });
      
      // Add unpaired connects - only the most recent one per user is active
      Object.keys(userConnections).forEach(userId => {
        const unpairConnects = userConnections[userId];
        
        unpairConnects.forEach((connectEvent, index) => {
          const isLastConnect = index === unpairConnects.length - 1;
          const connectTime = new Date(connectEvent.timestamp);
          const now = new Date();
          
          // Ensure accessedMcpServers and accessedWorkflows are arrays
          const accessedMcpServers = Array.isArray(connectEvent.accessedMcpServers) 
            ? connectEvent.accessedMcpServers 
            : (connectEvent.accessedMcpServers || []);
          const accessedWorkflows = Array.isArray(connectEvent.accessedWorkflows) 
            ? connectEvent.accessedWorkflows 
            : (connectEvent.accessedWorkflows || []);
          
          if (!isLastConnect) {
            // If this is not the last connect, infer a disconnect at the time of the next connect
            const nextConnectTime = new Date(unpairConnects[index + 1].timestamp);
            processedConnections.push({
              ...connectEvent,
              disconnectEvent: unpairConnects[index + 1],
              accessedMcpServers,
              accessedWorkflows,
              connectionDurationMs: nextConnectTime - connectTime,
              isPaired: false,
              isInferred: true // Inferred disconnect
            });
          } else {
            // Only the most recent connect is active
            processedConnections.push({
              ...connectEvent,
              accessedMcpServers,
              accessedWorkflows,
              connectionDurationMs: now - connectTime,
              isPaired: false,
              isActive: true // Mark as active connection
            });
          }
        });
      });
      
      setMcpConnections(processedConnections);
    } catch (err) {
      console.error('Error fetching MCP connections:', err);
    }
  };

  // Filter and sort login sessions
  const processedSessions = useMemo(() => {
    let filtered = [...sessions].filter(s => s.eventType !== 'mcp_proxy_connect' && s.eventType !== 'mcp_proxy_disconnect');

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(session =>
        session.user.username.toLowerCase().includes(term) ||
        session.user.email.toLowerCase().includes(term)
      );
    }

    // User filter
    if (selectedUser) {
      filtered = filtered.filter(session => session.user.id === selectedUser);
    }

    // Sorting
    filtered.sort((a, b) => {
      const [sortField, sortOrder] = sortBy.split('-');
      let aVal, bVal;

      switch (sortField) {
        case 'loginTime':
          aVal = new Date(a.loginTime);
          bVal = new Date(b.loginTime);
          break;
        case 'duration':
          aVal = a.connectionDurationMs || 0;
          bVal = b.connectionDurationMs || 0;
          break;
        case 'username':
          aVal = a.user.username;
          bVal = b.user.username;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [sessions, searchTerm, selectedUser, sortBy]);

  // Filter and sort MCP connections
  const processedMCPConnections = useMemo(() => {
    let filtered = [...mcpConnections].filter(conn => conn.user); // Filter out entries without user data

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(conn =>
        (conn.user && conn.user.username && conn.user.username.toLowerCase().includes(term)) ||
        (conn.user && conn.user.email && conn.user.email.toLowerCase().includes(term))
      );
    }

    // User filter
    if (selectedUser) {
      filtered = filtered.filter(conn => conn.user && conn.user.id === selectedUser);
    }

    // Sorting by timestamp
    filtered.sort((a, b) => {
      const aTime = new Date(a.timestamp || 0);
      const bTime = new Date(b.timestamp || 0);
      return bTime - aTime; // Newest first by default
    });

    return filtered;
  }, [mcpConnections, searchTerm, selectedUser]);

  // Pagination
  const paginatedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedSessions.slice(startIndex, startIndex + itemsPerPage);
  }, [processedSessions, currentPage, itemsPerPage]);

  const paginatedMCPConnections = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedMCPConnections.slice(startIndex, startIndex + itemsPerPage);
  }, [processedMCPConnections, currentPage, itemsPerPage]);

  const totalPagesLogins = Math.ceil(processedSessions.length / itemsPerPage);
  const totalPagesMCP = Math.ceil(processedMCPConnections.length / itemsPerPage);
  const totalPages = activeSubTab === 'logins' ? totalPagesLogins : totalPagesMCP;

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: isDarkMode ? '#aaa' : '#666' }}>Loading history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        backgroundColor: isDarkMode ? '#2a1a1a' : '#ffe6e6',
        borderRadius: '8px',
        border: `1px solid ${isDarkMode ? '#8b0000' : '#ff6666'}`,
        color: isDarkMode ? '#ffcccc' : '#cc0000'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div className="tab-content" style={{ minHeight: '600px' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{
          margin: 0,
          marginBottom: '1rem',
          color: isDarkMode ? 'white' : '#333',
          fontSize: '1.8rem',
          fontWeight: 'bold'
        }}>
          Historial
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => fetchData()}
            disabled={loading}
            style={{
              padding: '0.6rem 1.2rem',
              backgroundColor: '#0099FF',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: loading ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={handleSaveHistory}
            disabled={loading || (processedSessions.length === 0 && processedMCPConnections.length === 0)}
            style={{
              padding: '0.6rem 1.2rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || (processedSessions.length === 0 && processedMCPConnections.length === 0)) ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: (loading || (processedSessions.length === 0 && processedMCPConnections.length === 0)) ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            Save History
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={loading || deleting}
            style={{
              padding: '0.6rem 1.2rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || deleting) ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: (loading || deleting) ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            Delete History
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#28a745',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          minWidth: '300px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <span style={{ fontSize: '1.2rem' }}>✓</span>
          <span style={{ flex: 1 }}>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage('')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2rem',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              color: isDarkMode ? 'white' : '#333',
              marginBottom: '1rem',
              fontSize: '1.3rem'
            }}>
              Delete All History?
            </h3>
            <p style={{
              color: isDarkMode ? '#ccc' : '#666',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Are you sure you want to delete all login and MCP connection history? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.5rem',
                  backgroundColor: isDarkMode ? '#444' : '#e9ecef',
                  color: isDarkMode ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteHistory}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.5rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SubTab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: `2px solid ${isDarkMode ? '#333' : '#e9ecef'}`
      }}>
        {[
          { id: 'logins', label: 'Logins', icon: '🔐' },
          { id: 'mcp-connections', label: 'MCP Connections', icon: '🔌' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveSubTab(tab.id);
              setCurrentPage(1);
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              color: activeSubTab === tab.id 
                ? '#0099FF' 
                : (isDarkMode ? '#aaa' : '#666'),
              border: 'none',
              borderBottom: activeSubTab === tab.id 
                ? `3px solid #0099FF` 
                : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: activeSubTab === tab.id ? '600' : '400',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <input
          type="text"
          placeholder="Search by username or email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: '0.75rem',
            borderRadius: '6px',
            border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
            fontSize: '0.95rem'
          }}
        />

        <select
          value={selectedUser}
          onChange={(e) => {
            setSelectedUser(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: '0.75rem',
            borderRadius: '6px',
            border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
            fontSize: '0.95rem'
          }}
        >
          <option value="">All Users</option>
          {userList.map(user => (
            <option key={user.id} value={user.id}>
              {user.username}
            </option>
          ))}
        </select>

        {activeSubTab === 'logins' && (
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '0.75rem',
              borderRadius: '6px',
              border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
              backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
              color: isDarkMode ? '#fff' : '#000',
              fontSize: '0.95rem'
            }}
          >
            <option value="loginTime-desc">Login Time (Newest)</option>
            <option value="loginTime-asc">Login Time (Oldest)</option>
            <option value="duration-desc">Duration (Longest)</option>
            <option value="duration-asc">Duration (Shortest)</option>
            <option value="username-asc">Username (A-Z)</option>
          </select>
        )}
      </div>

      {/* Results Info */}
      <div style={{
        marginBottom: '1rem',
        fontSize: '0.9rem',
        color: isDarkMode ? '#aaa' : '#666'
      }}>
        Showing {activeSubTab === 'logins' 
          ? (paginatedSessions.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0) 
          : (paginatedMCPConnections.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0)
        } - {Math.min(
          activeSubTab === 'logins' ? currentPage * itemsPerPage : currentPage * itemsPerPage, 
          activeSubTab === 'logins' ? processedSessions.length : processedMCPConnections.length
        )} of {activeSubTab === 'logins' ? processedSessions.length : processedMCPConnections.length} items
      </div>

      {/* Logins Tab */}
      {activeSubTab === 'logins' && (
        <div style={{
          overflowX: 'auto',
          border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: isDarkMode ? '#2a2a2a' : '#fff'
          }}>
            <thead>
              <tr style={{
                backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
                borderBottom: `2px solid ${isDarkMode ? '#333' : '#e9ecef'}`
              }}>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>User</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>Login Time</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>Logout Time</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSessions.map((session, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
                    backgroundColor: index % 2 === 0 ? (isDarkMode ? '#2a2a2a' : '#fff') : (isDarkMode ? '#1a1a1a' : '#f8f9fa'),
                    transition: 'background-color 0.2s'
                  }}
                >
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333'
                  }}>
                    <div style={{ fontWeight: '500' }}>{session.user.username}</div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: isDarkMode ? '#aaa' : '#666'
                    }}>
                      {session.user.email}
                    </div>
                  </td>
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333',
                    fontSize: '0.9rem'
                  }}>
                    {formatDate(session.timestamp)}
                  </td>
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333',
                    fontSize: '0.9rem'
                  }}>
                    {session.logoutEvent ? (
                      session.isInferred ? (
                        <span style={{ color: isDarkMode ? '#888' : '#999', fontStyle: 'italic' }}>
                          {formatDate(session.logoutEvent.timestamp)} (inferred)
                        </span>
                      ) : (
                        formatDate(session.logoutEvent.timestamp)
                      )
                    ) : (
                      session.isActive ? <span style={{ color: '#ffc107', fontWeight: 'bold' }}>Still Active</span> : '—'
                    )}
                  </td>
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    {session.connectionDurationMs ? formatDuration(session.connectionDurationMs) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {processedSessions.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: isDarkMode ? '#aaa' : '#666'
            }}>
              No login history found
            </div>
          )}
        </div>
      )}

      {/* MCP Connections Tab */}
      {activeSubTab === 'mcp-connections' && (
        <div style={{
          overflowX: 'auto',
          border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: isDarkMode ? '#2a2a2a' : '#fff'
          }}>
            <thead>
              <tr style={{
                backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
                borderBottom: `2px solid ${isDarkMode ? '#333' : '#e9ecef'}`
              }}>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>User</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>Connect Time</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>Disconnect Time</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  color: isDarkMode ? '#fff' : '#333',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMCPConnections.map((conn, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
                    backgroundColor: index % 2 === 0 ? (isDarkMode ? '#2a2a2a' : '#fff') : (isDarkMode ? '#1a1a1a' : '#f8f9fa'),
                    transition: 'background-color 0.2s'
                  }}
                >
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333'
                  }}>
                    <div style={{ fontWeight: '500' }}>{conn.user.username}</div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: isDarkMode ? '#aaa' : '#666'
                    }}>
                      {conn.user.email}
                    </div>
                  </td>
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333',
                    fontSize: '0.9rem'
                  }}>
                    {formatDate(conn.timestamp)}
                  </td>
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333',
                    fontSize: '0.9rem'
                  }}>
                    {conn.disconnectEvent ? (
                      conn.isInferred ? (
                        <span style={{ color: isDarkMode ? '#888' : '#999', fontStyle: 'italic' }}>
                          {formatDate(conn.disconnectEvent.timestamp)} (inferred)
                        </span>
                      ) : (
                        formatDate(conn.disconnectEvent.timestamp)
                      )
                    ) : (
                      conn.isActive ? <span style={{ color: '#ffc107', fontWeight: 'bold' }}>Still Active</span> : '—'
                    )}
                  </td>
                  <td style={{
                    padding: '1rem',
                    color: isDarkMode ? '#ddd' : '#333',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    {conn.connectionDurationMs ? formatDuration(conn.connectionDurationMs) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {processedMCPConnections.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: isDarkMode ? '#aaa' : '#666'
            }}>
              No MCP connections found
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '2rem'
        }}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
              backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
              color: isDarkMode ? '#fff' : '#333',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            ← Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
                backgroundColor: page === currentPage ? '#0066cc' : (isDarkMode ? '#1a1a1a' : '#f8f9fa'),
                color: page === currentPage ? '#fff' : (isDarkMode ? '#fff' : '#333'),
                cursor: 'pointer',
                fontWeight: page === currentPage ? 'bold' : 'normal'
              }}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
              backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
              color: isDarkMode ? '#fff' : '#333',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default HistorialDashboard;
